// HAPPYCAP_AUTH_COMPACT_HEADER_V1
// HAPPYCAP_FEED_WIDTH_MOBILE_SEARCH_V1
// HAPPYCAP_NAV_BACKGROUND_AND_MOBILE_BOTTOM_V2
// HAPPYCAP_APP_NAV_HARMONIZED_V1
// HAPPYCAP_AUTH_PAGES_TRANSPARENT_NAV_V1
// HAPPYCAP_LOGIN_BLUE_UNIFIED_V1
// HAPPYCAP_NAV_ALIGNED_RESOURCES_V1
import { FEATURES } from "../config/features";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useSearch } from "../context/SearchContext";
import { useNotification } from "../context/NotificationContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Home,
  Coins,
  Send,
  Users,
  User,
  Images,
  Bell,
  MessagesSquare,
  Search,
  BookOpenText,
} from "lucide-react";

const Navbar = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { setQuery } = useSearch();
  const { notificaciones, unreadBySender } = useNotification();
  const [input, setInput] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // HAPPYCAP_LOGIN_TRANSPARENT_NAV_V1
  const isLoginPage = [
    "/",
    "/login",
    "/reset-password",
    "/signup",
    "/signup/menor",
    "/signup/self",
  ].includes(
    location.pathname.replace(/\/$/, "") || "/"
  ) && !isAuthenticated;


  const handleSearch = (e) => {
    e.preventDefault();
    if (input.trim()) {
      setQuery(input.trim());
      navigate("/search");
      setInput("");
    }
  };

  const totalChatsConNotificaciones =
    unreadBySender?.reduce((acc, item) => acc + (item.count || 0), 0) || 0;

  return (
    <nav
      className={`fixed top-0 z-50 w-full transition-colors duration-200 ${
        isLoginPage
          ? "bg-transparent text-[#247BA8] shadow-none"
          : "bg-[#DCECF9] text-[#163F63] shadow-none border-b border-[#DCECF9]"
      }`}
      role="navigation"
      aria-label="Barra de navegación principal"
    >
      <div className={`max-w-6xl mx-auto px-6 ${(location.pathname === "/" || ["/login", "/reset-password", "/signup", "/signup/menor", "/signup/self"].includes(location.pathname.replace(/\/$/, ""))) && !isAuthenticated ? "py-0" : "py-2"}`}>
        {/* --- Desktop: Logo + Iconos + Logout --- */}
        <div className="hidden md:flex w-full min-w-0 items-center justify-between gap-3 lg:gap-5">
          {/* Logo */}
          <div className={`shrink-0 text-left ${(location.pathname === "/" || ["/login", "/reset-password", "/signup", "/signup/menor", "/signup/self"].includes(location.pathname.replace(/\/$/, ""))) ? "hidden" : ""}`}>
            <Link to={isAuthenticated ? "/info" : "/"}>
              {isLoginPage ? (
                <span
                  role="img"
                  aria-label="Happycap"
                  className="block h-12 w-12 bg-[#247BA8] transition-opacity hover:opacity-80"
                  style={{
                    WebkitMaskImage: "url('/img/logo.png')",
                    maskImage: "url('/img/logo.png')",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
              ) : (
                <span
                  role="img"
                  aria-label="Happycap"
                  className="block h-12 w-12 bg-[#163F63] transition-opacity hover:opacity-80"
                  style={{
                    WebkitMaskImage: "url('/img/logo.png')",
                    maskImage: "url('/img/logo.png')",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
              )}
            </Link>
          </div>

          {/* Iconos centrados */}
          {isAuthenticated && (
            <div className="flex min-w-0 flex-1 items-center justify-center gap-1 lg:gap-2 xl:gap-4 text-[#163F63]">
              <IconLink
                to="/recursos"
                icon={<BookOpenText className="w-8 h-8" />}
                label={t("navbar.resources")}
              />
              <IconLink
                to="/feed"
                icon={<Home className="w-8 h-8" />}
                label={t("navbar.feed")}
              />
              <IconLink
                to="/buscar"
                icon={<Users className="w-8 h-8" />}
                label={t("navbar.search_people")}
              />
              <IconLink
                to="/profile"
                icon={<User className="w-8 h-8" />}
                label={t("navbar.profile")}
              />
              <IconLink
                to="/gallery"
                icon={<Images className="w-8 h-8" />}
                label={t("navbar.gallery")}
              />
              <IconLink
                to="/chat"
                icon={<MessagesSquare className="w-8 h-8" />}
                badge={totalChatsConNotificaciones}
                label={t("navbar.chat")}
              />
              {FEATURES.monetization && (<IconLink
                to="/monetizacion"
                icon={<Coins className="w-8 h-8" />}
                label={t("navbar.earnings")}
              />
	      )}
              <IconLink
                to="/solicitudes"
                icon={<Bell className="w-8 h-8" />}
                badge={notificaciones}
                label={t("navbar.requests")}
              />
            </div>
          )}


          {/* HAPPYCAP_DESKTOP_NAV_SEARCH_INLINE_V1 */}
          {isAuthenticated && (
            <form
              onSubmit={handleSearch}
              role="search"
              className="group ml-auto flex h-10 w-[160px] shrink-0 items-center gap-2 rounded-full border border-white/70 bg-white px-3 shadow-sm transition-all duration-200 focus-within:border-[#B5DCEB] focus-within:shadow-md lg:w-[190px] xl:w-[220px]"
            >
              <Search
                className="h-[18px] w-[18px] shrink-0 text-[#247BA8]"
                aria-hidden="true"
              />

              <input
                type="search"
                placeholder={t("navbar.search_placeholder")}
                aria-label={t("navbar.search_placeholder")}
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />

              <button
                type="submit"
                disabled={!input.trim()}
                className="sr-only"
                aria-label={t("navbar.search_placeholder")}
              >
                {t("navbar.search_placeholder")}
              </button>
            </form>
          )}

        </div>

        {/* --- Móvil: Logo + Buscador + Logout (en una sola fila) --- */}
        <div className="flex md:hidden items-center justify-between w-full gap-2">
          {/* Logo */}
          <div className={`flex-shrink-0 ${(location.pathname === "/" || ["/login", "/reset-password", "/signup", "/signup/menor", "/signup/self"].includes(location.pathname.replace(/\/$/, ""))) ? "hidden" : ""}`}>
            <Link to={isAuthenticated ? "/info" : "/"}>
              {isLoginPage ? (
                <span
                  role="img"
                  aria-label="Happycap"
                  className="block h-10 w-10 bg-[#247BA8] transition-opacity hover:opacity-80"
                  style={{
                    WebkitMaskImage: "url('/img/logo.png')",
                    maskImage: "url('/img/logo.png')",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
              ) : (
                <span
                  role="img"
                  aria-label="Happycap"
                  className="block h-10 w-10 bg-[#163F63] transition-opacity hover:opacity-80"
                  style={{
                    WebkitMaskImage: "url('/img/logo.png')",
                    maskImage: "url('/img/logo.png')",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />
              )}
            </Link>
          </div>

          {/* Buscador móvil */}
          {isAuthenticated && (
            <div className="flex-1 flex justify-center px-2">
              <form
                onSubmit={handleSearch}
                className="flex items-center bg-white rounded-full px-2 py-1 shadow-inner w-full max-w-[180px] sm:max-w-[240px]"
              >
                <input
                  type="text"
                  placeholder={t("navbar.search_placeholder")}
                  className="text-black bg-transparent outline-none px-1 text-sm flex-1"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
                
              </form>
            </div>
          )}


        </div>
      </div>

      {/* --- Menú móvil fijo abajo --- */}
      {isAuthenticated && (
        <div
          className="h-[64px] fixed bottom-0 left-0 right-0 z-50 border-t flex justify-around items-center py-2 md:hidden bg-[#DCECF9] text-[#163F63] shadow-[0_-2px_10px_rgba(22,63,99,0.08)] border-[#C5DDEA]"
          role="navigation"
          aria-label="Menú de navegación móvil"
        >
          <IconLink
            to="/recursos"
            icon={<BookOpenText className="w-6 h-6" />}
            label={t("navbar.resources")}
          />
          <IconLink
            to="/feed"
            icon={<Home className="w-6 h-6" />}
            label={t("navbar.feed")}
          />
          <IconLink
            to="/buscar"
            icon={<Users className="w-6 h-6" />}
            label={t("navbar.search_people")}
          />
          <IconLink
            to="/chat"
            icon={<MessagesSquare className="w-6 h-6" />}
            badge={totalChatsConNotificaciones}
            label={t("navbar.chat")}
          />
          <IconLink
            to="/profile"
            icon={<User className="w-6 h-6" />}
            label={t("navbar.profile")}
          />
          <IconLink
            to="/gallery"
            icon={<Images className="w-6 h-6" />}
            label={t("navbar.gallery")}
          />
          {FEATURES.monetization && (<IconLink
            to="/monetizacion"
            icon={<Coins className="w-6 h-6" />}
            label={t("navbar.earnings")}
          />
	  )}
          <IconLink
            to="/solicitudes"
            icon={<Bell className="w-6 h-6" />}
            badge={notificaciones}
            label={t("navbar.requests")}
          />
        </div>
      )}
    </nav>
  );
};

// Adapted IconLink
const IconLink = ({ to, icon, badge, label }) => (
  <div className="relative group">
    <Link
      to={to}
      className="inline-flex items-center justify-center w-10 h-10 rounded-full text-inherit transition hover:bg-[#C5DDEA]"
      aria-label={label}
      title={label}
    >
      {icon}
      <span className="sr-only">{label}</span>
    </Link>
    {badge > 0 && (
      <span
        className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full shadow-md"
        aria-label={`${badge} ${label}`}
      >
        {badge}
      </span>
    )}
  </div>
);

export default Navbar;
